import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize?: number;
  totalElements?: number;
  className?: string;
  withoutTotal?: boolean;
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];

export const TablePagination: FC<PaginationProps> = ({
  currentPage,
  totalPages,
  pageSize = 20,
  totalElements = 0,
  className,
  withoutTotal,
}) => {
  const { t } = useTranslation();
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  const [searchParams, setSearchParams] = useSearchParams();

  const handlePageChange = (page: number | string) => {
    const pageNumber = Number(page);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('page', String(page));
          return newParams;
        },
        { replace: true }, // Use replace to prevent adding to history
      );
    }
  };

  const handleSizeChange = (value: string) => {
    setSearchParams((prev) => {
      prev.set('size', value);
      prev.set('page', '1');
      return prev;
    });
  };

  // Generate array of page numbers to display
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const renderPageNumbers = () =>
    getPageNumbers().map((pageNumber, index) =>
      pageNumber === '...' ? (
        <span key={`dots-${index}`} className="px-2">
          ...
        </span>
      ) : (
        <Button
          type="button"
          key={pageNumber}
          variant={currentPage === pageNumber ? 'default' : 'outline'}
          className={`h-8 w-8 p-0 border-gray-400 ${
            currentPage === pageNumber
              ? 'bg-primary hover:bg-primary text-white'
              : ''
          }`}
          onClick={() => handlePageChange(pageNumber)}
        >
          {pageNumber}
        </Button>
      ),
    );

  return (
    <div
      className={`flex items-center justify-between px-6 py-3 border-t  ${className}`}
    >
      {!withoutTotal && (
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="">
            {t('total')}: {totalElements}
          </span>
        </div>
      )}

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 ">
          <span>{t('page.size')}:</span>
          <Select value={String(pageSize)} onValueChange={handleSizeChange}>
            <SelectTrigger className="w-20 h-8 ">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              className="size-8 p-0"
              onClick={() => handlePageChange(1)}
              disabled={!canGoPrevious}
            >
              <ChevronsLeft size={21} />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="size-8 p-0"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!canGoPrevious}
            >
              <ChevronLeft size={18} />
            </Button>

            {renderPageNumbers()}

            <Button
              type="button"
              variant="outline"
              className="size-8 p-0"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!canGoNext}
            >
              <ChevronRight size={18} />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="size-8 p-0"
              onClick={() => handlePageChange(totalPages)}
              disabled={!canGoNext}
            >
              <ChevronsRight size={21} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
